/**
 * NodeDetails Component - Exibe detalhes do nó selecionado
 * Baseado em UX research: F-pattern, informações críticas no topo
 *
 * Layout:
 * - Cabeçalho com tipo e valor principal
 * - Dados detalhados (depende do tipo)
 * - Ações (expandir, centralizar, copiar)
 * - Relacionamentos (se houver)
 *
 * Mobile: Bottom sheet ou sidebar
 * Desktop: Sidebar direito
 */

import { NodeSingular } from 'cytoscape';
import {
  BuildingOfficeIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  IdentificationIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { formatCNPJ, formatCPF, formatTelefone, formatDate } from '../../utils/formatters';
import type { TipoNo } from '../../types/investigation';

interface NodeDetailsProps {
  node: NodeSingular | null;
  onExpand?: (nodeId: string) => void;
  onCenter?: (nodeId: string) => void;
  onClose?: () => void;
}

/**
 * Painel de detalhes do nó selecionado
 *
 * Features:
 * - ✅ Ícones por tipo de nó
 * - ✅ Formatação de dados (CNPJ, CPF, telefone)
 * - ✅ Botões de ação (expandir, centralizar, copiar)
 * - ✅ Dados adicionais do backend
 * - ✅ Indicador de status (expandido/não expandido)
 *
 * @example
 * <NodeDetails
 *   node={selectedNode}
 *   onExpand={(id) => expandNode(investigationId, id)}
 *   onCenter={(id) => centerNode(id)}
 * />
 */
export function NodeDetails({ node, onExpand, onCenter }: NodeDetailsProps) {
  if (!node) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <div className="text-center text-gray-500">
          <p className="text-sm">Selecione um nó no grafo para ver detalhes</p>
        </div>
      </div>
    );
  }

  const type = node.data('type') as TipoNo;
  const label = node.data('label') as string;
  const rawData = node.data('rawData') as Record<string, any>;
  const isExpanded = node.data('expandido') as boolean;
  const nivel = node.data('nivel') as number;

  return (
    <div className="flex flex-col h-full">
      {/* Header - Tipo e valor principal */}
      <div className="px-4 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-start space-x-3">
          {/* Ícone do tipo */}
          <div className="flex-shrink-0">
            <NodeTypeIcon type={type} className="h-8 w-8 text-blue-600" />
          </div>

          {/* Info principal */}
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
              {getTypeLabel(type)}
            </p>
            <h3 className="text-lg font-semibold text-gray-900 mt-1 break-words">
              {label}
            </h3>

            {/* Status badge */}
            <div className="mt-2 flex items-center space-x-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                isExpanded
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {isExpanded ? 'Expandido' : 'Não expandido'}
              </span>
              <span className="text-xs text-gray-500">
                Nível {nivel}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content - Dados detalhados (scrollable) */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* Renderiza detalhes específicos por tipo */}
        {type === 'cnpj' && <CNPJDetails data={rawData} />}
        {type === 'pessoa' && <PessoaDetails data={rawData} />}
        {type === 'email' && <EmailDetails data={rawData} />}
        {type === 'telefone' && <TelefoneDetails data={rawData} />}
        {type === 'cpf-nome' && <CPFNomeDetails data={rawData} />}

        {/* Dados brutos (opcional - para debug) */}
        {import.meta.env.DEV && Object.keys(rawData).length > 0 && (
          <details className="mt-4">
            <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
              Dados brutos (dev only)
            </summary>
            <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-x-auto">
              {JSON.stringify(rawData, null, 2)}
            </pre>
          </details>
        )}
      </div>

      {/* Footer - Ações */}
      <div className="px-4 py-4 border-t border-gray-200 space-y-2">
        {/* Expandir nó (se não expandido) */}
        {!isExpanded && onExpand && (
          <Button
            variant="primary"
            size="md"
            onClick={() => onExpand(node.id())}
            icon={<ArrowsPointingOutIcon className="h-5 w-5" />}
            className="w-full"
          >
            Expandir Nó
          </Button>
        )}

        {/* Centralizar no grafo */}
        {onCenter && (
          <Button
            variant="secondary"
            size="md"
            onClick={() => onCenter(node.id())}
            icon={<ArrowsPointingInIcon className="h-5 w-5" />}
            className="w-full"
          >
            Centralizar no Grafo
          </Button>
        )}

        {/* Copiar ID */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            navigator.clipboard.writeText(node.id());
          }}
          icon={<ClipboardDocumentIcon className="h-5 w-5" />}
          className="w-full"
        >
          Copiar ID
        </Button>
      </div>
    </div>
  );
}

// ============ Detalhes específicos por tipo ============

function CNPJDetails({ data }: { data: Record<string, any> }) {
  return (
    <div className="space-y-3">
      <DetailItem label="CNPJ" value={data.cnpj ? formatCNPJ(data.cnpj) : '-'} />
      <DetailItem label="Razão Social" value={data.razao_social} />
      <DetailItem label="Nome Fantasia" value={data.nome_fantasia} />
      <DetailItem label="Situação Cadastral" value={data.situacao_cadastral} />
      <DetailItem label="Data de Abertura" value={data.data_abertura ? formatDate(data.data_abertura, false) : '-'} />
      <DetailItem label="Natureza Jurídica" value={data.natureza_juridica} />
      <DetailItem label="Porte" value={data.porte} />
      <DetailItem label="Capital Social" value={data.capital_social} />

      {/* Endereço */}
      {data.logradouro && (
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs font-medium text-gray-700 mb-1">Endereço</p>
          <p className="text-sm text-gray-900">
            {data.logradouro}, {data.numero}
            {data.complemento && ` - ${data.complemento}`}
          </p>
          <p className="text-sm text-gray-600">
            {data.bairro} - {data.municipio}/{data.uf}
          </p>
          <p className="text-sm text-gray-600">CEP: {data.cep}</p>
        </div>
      )}
    </div>
  );
}

function PessoaDetails({ data }: { data: Record<string, any> }) {
  return (
    <div className="space-y-3">
      <DetailItem label="Nome" value={data.nome} />
      <DetailItem label="CPF" value={data.cpf ? formatCPF(data.cpf) : '-'} />
      <DetailItem label="Qualificação" value={data.qualificacao} />
      <DetailItem label="Data de Entrada" value={data.data_entrada ? formatDate(data.data_entrada, false) : '-'} />
      <DetailItem label="País" value={data.pais} />
    </div>
  );
}

function EmailDetails({ data }: { data: Record<string, any> }) {
  return (
    <div className="space-y-3">
      <DetailItem label="Email" value={data.email} copyable />
      <DetailItem label="Tipo" value={data.tipo} />
      <DetailItem label="Primeiro registro" value={data.primeira_vez ? formatDate(data.primeira_vez) : '-'} />
    </div>
  );
}

function TelefoneDetails({ data }: { data: Record<string, any> }) {
  return (
    <div className="space-y-3">
      <DetailItem label="Telefone" value={data.telefone ? formatTelefone(data.telefone) : '-'} copyable />
      <DetailItem label="Tipo" value={data.tipo} />
      <DetailItem label="DDD" value={data.ddd} />
      <DetailItem label="Primeiro registro" value={data.primeira_vez ? formatDate(data.primeira_vez) : '-'} />
    </div>
  );
}

function CPFNomeDetails({ data }: { data: Record<string, any> }) {
  return (
    <div className="space-y-3">
      <DetailItem label="CPF" value={data.cpf ? formatCPF(data.cpf) : '-'} copyable />
      <DetailItem label="Nome" value={data.nome} />
    </div>
  );
}

// ============ Helper Components ============

interface DetailItemProps {
  label: string;
  value?: string | number | null;
  copyable?: boolean;
}

function DetailItem({ label, value, copyable }: DetailItemProps) {
  if (!value) return null;

  return (
    <div>
      <dt className="text-xs font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 flex items-center justify-between">
        <span className="break-words flex-1">{value}</span>
        {copyable && (
          <button
            onClick={() => navigator.clipboard.writeText(String(value))}
            className="ml-2 p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 transition-colors"
            title="Copiar"
          >
            <ClipboardDocumentIcon className="h-4 w-4" />
          </button>
        )}
      </dd>
    </div>
  );
}

function NodeTypeIcon({ type, className }: { type: TipoNo; className?: string }) {
  const icons = {
    cnpj: BuildingOfficeIcon,
    pessoa: UserIcon,
    email: EnvelopeIcon,
    telefone: PhoneIcon,
    'cpf-nome': IdentificationIcon,
  };

  const Icon = icons[type];
  return <Icon className={className} />;
}

function getTypeLabel(type: TipoNo): string {
  const labels: Record<TipoNo, string> = {
    cnpj: 'CNPJ - Empresa',
    pessoa: 'Pessoa Física',
    email: 'Email',
    telefone: 'Telefone',
    'cpf-nome': 'CPF + Nome',
  };
  return labels[type];
}
